import { useTripBuilder } from '../TripBuilderContext';
import { getTransportInfo, EFFORT_STYLE } from '../transport';
import { getRestaurantLinks, getBookingWindow } from '../restaurantLinks';
import bookingLinks from '../bookingLinks';

// Single chronological day-by-day view of the entire planned trip.
//
// Walks the timeline from departure day → return home, allocating each
// day to a specific stop (Lisbon days 1-4, Porto days 5-7, etc.).
// Surfaces flights/transport on transition days, hotel check-in/out
// markers, activities + restaurants slotted into morning/afternoon/
// evening, plus inline deep-links to every booking site.
//
// This is the "everything in one view" page the user can scroll through
// once they've planned, with concrete actions per day.

const SOURCE_LABELS = {
  amex_mr: 'Amex MR', chase_ur: 'Chase UR',
  capital_one: 'Capital One Venture', citi_ty: 'Citi ThankYou', bilt: 'Bilt',
};

function fmtDate(d) {
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function buildItinerary(tb) {
  if (!tb.startDate || !tb.endDate || tb.stops.length === 0) return { days: [], legs: [] };

  const startDate = new Date(tb.startDate + 'T12:00:00');
  const totalNights = Math.max(1, Math.round((new Date(tb.endDate) - new Date(tb.startDate)) / 86400000));

  // Map each day index → which stop it belongs to. Day 0 = arrival day at
  // stop 0; days continue at stop 0 until its nights are used; then we
  // transition to stop 1, etc.
  const dayCount = totalNights + 1; // include return-departure day
  const dayStopIndex = [];
  let stopIdx = 0;
  let nightsUsed = 0;
  for (let d = 0; d < dayCount; d++) {
    dayStopIndex.push(stopIdx);
    if (d > 0 && d <= totalNights) {
      nightsUsed++;
      const currentStopNights = tb.stops[stopIdx]?.nights || totalNights;
      if (nightsUsed >= currentStopNights && stopIdx < tb.stops.length - 1) {
        stopIdx++;
        nightsUsed = 0;
      }
    }
  }

  const days = [];
  for (let i = 0; i < dayCount; i++) {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);
    const stopI = dayStopIndex[i];
    const stop = tb.stops[stopI];
    const isFirstDayAtStop = i === 0 || dayStopIndex[i - 1] !== stopI;
    const isLastDayAtStop = i === dayCount - 1 || dayStopIndex[i + 1] !== stopI;
    const isTripStart = i === 0;
    const isTripEnd = i === dayCount - 1;
    const stopNights = stop?.nights || 1;
    const fairDayWithin = (() => {
      const stopStart = dayStopIndex.indexOf(stopI);
      return i - stopStart;
    })();

    days.push({
      _stopIdx: stopI,
      dayNum: i + 1,
      date,
      stop,
      isFirstDayAtStop,
      isLastDayAtStop,
      isTripStart,
      isTripEnd,
      fairDayWithin,
      stopNights: stopNights + (isFirstDayAtStop && i > 0 ? 1 : 0),
      activities: [],
      restaurants: [],
      events: [],
    });
  }

  // Distribute the stop's activities across its days
  for (let stopI = 0; stopI < tb.stops.length; stopI++) {
    const stop = tb.stops[stopI];
    const stopDays = days.filter(d => d._stopIdx === stopI);
    const acts = stop.activities || [];
    const rests = stop.restaurants || [];
    acts.forEach((a, idx) => {
      const dayIdx = idx % stopDays.length;
      stopDays[dayIdx].activities.push(a);
    });
    rests.forEach((r, idx) => {
      const dayIdx = idx % stopDays.length;
      stopDays[dayIdx].restaurants.push(r);
    });
  }

  return { days, legs: tb.legs };
}

function DayCard({ day, tb, legs }) {
  const { stop, isFirstDayAtStop, isLastDayAtStop, isTripStart, isTripEnd } = day;
  if (!stop) return null;

  // Find leg for this day if any (arriving / departing flight)
  const arrivalLeg = (() => {
    if (isTripStart) return legs.find(l => l.kind === 'outbound');
    if (isFirstDayAtStop) {
      // Inter-city leg ending at this stop (one of the inter-city legs)
      return legs.find(l => l.kind === 'intercity' && l.index === day._stopIdx);
    }
    return null;
  })();
  const departureLeg = isTripEnd ? legs.find(l => l.kind === 'return') : null;
  const nextStop = isLastDayAtStop && !isTripEnd ? tb.stops[day._stopIdx + 1] : null;
  const interTransport = nextStop ? getTransportInfo(stop.destination.city, nextStop.destination.city) : null;

  return (
    <div className="bg-navy-light border border-brand-ink/10 rounded-2xl overflow-hidden">
      {/* Day header */}
      <div className="bg-brand-ink/5 px-4 py-3 border-b border-brand-ink/10 flex items-baseline justify-between gap-3">
        <div className="flex items-baseline gap-3">
          <div className="text-2xl font-black text-teal">Day {day.dayNum}</div>
          <div>
            <div className="text-sm font-bold text-brand-ink">{fmtDate(day.date)}</div>
            <div className="text-xs text-brand-ink/65">{stop.destination.city}</div>
          </div>
        </div>
        {isFirstDayAtStop && !isTripStart && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-teal/15 text-teal border border-teal/30 font-bold">
            Arrival
          </span>
        )}
        {isLastDayAtStop && !isTripEnd && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 font-bold">
            Last day here
          </span>
        )}
        {isTripEnd && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30 font-bold">
            Head home
          </span>
        )}
      </div>

      <div className="p-4 space-y-3">

        {/* Arrival flight / transport event */}
        {arrivalLeg && (
          <DayEvent
            icon="✈️"
            label={arrivalLeg.flight ? `Arrive on ${arrivalLeg.flight.airline_program}` : `Arrive in ${stop.destination.city}`}
            details={
              arrivalLeg.flight
                ? `${arrivalLeg.origin} → ${arrivalLeg.destinationCodes[0]} · ${tb.scaleForTravelers(arrivalLeg.flight.total_points_two_travelers).toLocaleString()} pts + $${tb.scaleForTravelers(arrivalLeg.flight.total_estimated_taxes)} taxes`
                : `${arrivalLeg.origin} → ${arrivalLeg.destinationCodes[0]} — pick a flight on the Flights step`
            }
            color="teal"
            actions={
              arrivalLeg.flight && bookingLinks[arrivalLeg.flight.source_program === 'chase_ur' ? 'chase_ur' : 'amex_mr']
                ? [{
                    label: `${SOURCE_LABELS[arrivalLeg.flight.source_program === 'chase_ur' ? 'chase_ur' : 'amex_mr']} transfer portal`,
                    url: bookingLinks[arrivalLeg.flight.source_program === 'chase_ur' ? 'chase_ur' : 'amex_mr'].transfer,
                  }]
                : null
            }
          />
        )}

        {/* If first day of trip but no outbound picked, prompt */}
        {isTripStart && !arrivalLeg?.flight && legs.length === 0 && (
          <DayEvent
            icon="⚠️"
            label="No outbound flight picked yet"
            details={`From ${tb.userConfig.origin || 'JFK'} to ${stop.destination.city}. Go to Plan Trip → Flights to search live award seats.`}
            color="amber"
          />
        )}

        {/* Hotel check-in marker on arrival day */}
        {isFirstDayAtStop && stop.hotel && (
          <DayEvent
            icon="🏨"
            label={`Check in: ${stop.hotel.hotel_name}`}
            details={`${stop.hotel.hotel_program} · ${stop.nights}n · ${stop.hotel.total_points?.toLocaleString()} pts total`}
            color="indigo"
            actions={
              bookingLinks[stop.hotel.hotel_program]
                ? [{ label: `${stop.hotel.hotel_program} booking site`, url: bookingLinks[stop.hotel.hotel_program].search }]
                : null
            }
          />
        )}
        {isFirstDayAtStop && !stop.hotel && !isTripEnd && (
          <DayEvent
            icon="⚠️"
            label="No hotel picked for this city"
            details={`Pick one on Plan Trip → Hotel step. ${stop.nights} night${stop.nights === 1 ? '' : 's'} needed here.`}
            color="amber"
          />
        )}

        {/* Activities for this day */}
        {day.activities.map((a) => (
          <DayEvent
            key={'act' + a.id}
            icon={a.category === 'food_drink' ? '🍷' : a.category === 'beaches' ? '🏖️' : a.category === 'culture' ? '🏛️' : a.category === 'tour' ? '👣' : a.category === 'excursion' ? '🗺️' : '✨'}
            label={a.name}
            details={`${a.duration || ''} · $${tb.scaleForTravelers(a.price_for_two)} for ${tb.travelersLabel}${a.booking_tip ? ' · ' + a.booking_tip : ''}`}
            color="teal"
          />
        ))}

        {/* Restaurants for this day */}
        {day.restaurants.map((r) => {
          const links = getRestaurantLinks(r);
          const win = getBookingWindow(r);
          return (
            <DayEvent
              key={'rest' + r.id}
              icon="🍽️"
              label={r.name}
              details={`${r.cuisine} · ${r.price_range} · $${tb.scaleForTravelers(r.avg_meal_for_two)} for ${tb.travelersLabel} · Book ${win.window}${win.urgent ? ' ⚡' : ''}`}
              color="gold"
              actions={links.slice(0, 3).map(l => ({ label: l.label, url: l.url, icon: l.icon }))}
            />
          );
        })}

        {/* Inter-city transport at end of stay */}
        {nextStop && interTransport && (
          <DayEvent
            icon={interTransport.mode === 'Flight' ? '✈️' : interTransport.mode.includes('Train') || interTransport.mode === 'TGV' || interTransport.mode === 'Thalys' || interTransport.mode === 'Eurostar' ? '🚄' : '🚆'}
            label={`Travel to ${nextStop.city}`}
            details={`${interTransport.mode} · ~${interTransport.duration} · ${interTransport.cost}${EFFORT_STYLE[interTransport.effort] ? ' · ' + EFFORT_STYLE[interTransport.effort].label : ''}. ${interTransport.note}`}
            color="amber"
          />
        )}

        {/* Departure flight */}
        {departureLeg && (
          <DayEvent
            icon="✈️"
            label={departureLeg.flight ? `Depart on ${departureLeg.flight.airline_program}` : `Depart for ${tb.userConfig.origin || 'JFK'}`}
            details={
              departureLeg.flight
                ? `${departureLeg.origin} → ${departureLeg.destinationCodes[0]} · ${tb.scaleForTravelers(departureLeg.flight.total_points_two_travelers).toLocaleString()} pts + $${tb.scaleForTravelers(departureLeg.flight.total_estimated_taxes)} taxes`
                : `${departureLeg.origin} → ${departureLeg.destinationCodes[0]} — pick a return flight on the Flights step`
            }
            color="rose"
            actions={
              departureLeg.flight && bookingLinks[departureLeg.flight.source_program === 'chase_ur' ? 'chase_ur' : 'amex_mr']
                ? [{
                    label: `${SOURCE_LABELS[departureLeg.flight.source_program === 'chase_ur' ? 'chase_ur' : 'amex_mr']} transfer portal`,
                    url: bookingLinks[departureLeg.flight.source_program === 'chase_ur' ? 'chase_ur' : 'amex_mr'].transfer,
                  }]
                : null
            }
          />
        )}

        {/* Empty day fallback */}
        {day.activities.length === 0 && day.restaurants.length === 0 && !arrivalLeg && !departureLeg && !nextStop && (
          <div className="text-xs text-brand-ink/55 italic">
            Free day — explore on your own, take a rest day, or add an activity/restaurant from Plan Trip.
          </div>
        )}

      </div>
    </div>
  );
}

function DayEvent({ icon, label, details, color = 'teal', actions }) {
  const colorClass = {
    teal: 'border-teal/25 bg-teal/5',
    indigo: 'border-indigo-500/25 bg-indigo-500/5',
    gold: 'border-gold/25 bg-gold/5',
    amber: 'border-amber-500/25 bg-amber-500/5',
    rose: 'border-rose-500/25 bg-rose-500/5',
  }[color] || 'border-brand-ink/15 bg-brand-ink/5';
  return (
    <div className={`border rounded-lg p-2.5 ${colorClass}`}>
      <div className="flex items-start gap-2">
        <span className="text-base shrink-0">{icon}</span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-brand-ink">{label}</div>
          <div className="text-[11px] text-brand-ink/70 mt-0.5 leading-snug">{details}</div>
          {actions && actions.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {actions.map(a => (
                <a
                  key={a.label}
                  href={a.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded border bg-brand-ink/10 border-brand-ink/15 text-brand-ink hover:bg-brand-ink/15 transition"
                >
                  {a.icon && <span>{a.icon}</span>}
                  <span>{a.label} ↗</span>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ItineraryView() {
  const tb = useTripBuilder();
  const { days, legs } = buildItinerary(tb);
  if (days.length === 0) return null;
  return (
    <div className="space-y-3">
      {days.map((d) => (
        <DayCard key={d.dayNum} day={d} tb={tb} legs={legs} />
      ))}
    </div>
  );
}
